import { createContext, useContext, useEffect, useState } from 'react';
import { firebaseAuth, googleProvider } from '@/lib/firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type User as FirebaseUser,
} from 'firebase/auth';
// Import safely - we need to handle the case where this might be used outside ImpersonationProvider during init
import { useImpersonation } from './ImpersonationContext';

interface AuthContextType {
  user: FirebaseUser | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Try to use impersonation context, but fail gracefully if not available yet
  let impersonation;
  try {
    impersonation = useImpersonation();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    // Ignore error if used outside provider (e.g. tests)
  }

  const isImpersonatingLoggedOut = impersonation?.impersonatedRole === 'logged-out';

  useEffect(() => {
    // Listen for Firebase auth state changes
    const unsubscribe = onAuthStateChanged(firebaseAuth, (firebaseUser) => {
      console.log('Firebase auth state changed:', {
        userId: firebaseUser?.uid,
        email: firebaseUser?.email,
        provider: firebaseUser?.providerData[0]?.providerId,
      });
      setUser(firebaseUser);
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const value = {
    user: isImpersonatingLoggedOut ? null : user,
    loading,

    /**
     * Sign up with email and password
     */
    signUp: async (email: string, password: string) => {
      try {
        const userCredential = await createUserWithEmailAndPassword(
          firebaseAuth,
          email,
          password
        );
        console.log('User signed up successfully:', userCredential.user.uid);
        return { error: null };
      } catch (error) {
        console.error('Sign up error:', error);
        return { error: error as Error };
      }
    },

    /**
     * Sign in with email and password
     */
    signIn: async (email: string, password: string) => {
      try {
        const userCredential = await signInWithEmailAndPassword(
          firebaseAuth,
          email,
          password
        );
        console.log('User signed in successfully:', userCredential.user.uid);
        return { error: null };
      } catch (error) {
        console.error('Sign in error:', error);
        return { error: error as Error };
      }
    },

    /**
     * Sign in with Google
     */
    signInWithGoogle: async () => {
      try {
        const result = await signInWithPopup(firebaseAuth, googleProvider);
        console.log('User signed in with Google:', result.user.uid);
        return { error: null };
      } catch (error) {
        console.error('Google sign in error:', error);
        return { error: error as Error };
      }
    },

    /**
     * Sign out
     */
    signOut: async () => {
      try {
        await firebaseSignOut(firebaseAuth);
        console.log('User signed out successfully');
        return { error: null };
      } catch (error) {
        console.error('Sign out error:', error);
        return { error: error as Error };
      }
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
